import styled from "styled-components";
import type {ReactNode} from "react";
import {useState} from "react";
import {getMassUpdateOptions} from "../../../../modules/abstract/sidepanel/tabs/details/field/mass/IMassInput";
import {useAppStore} from "../../../../../StateManager";
import {FieldV5} from "../../FieldV5";
import {ReactUtils} from "../../../../utils/ReactUtils";
import type {IModel} from "../../../../../data/models/Model";
import type {FieldIconName} from "../../../../modules/abstract/sidepanel/tabs/details/field/FieldInputUtils";
import {getIconComponentFromFieldIconName, getTooltipByIcon} from "../../../../modules/abstract/sidepanel/tabs/details/field/FieldInputUtils";
import type {IFieldAdapter} from "../../../../../data/models/field/Field";
import {colorPalette} from "../../../styles/colorPalette";
import {baseDistance, FlexCenterStyle, radius} from "../../../styles/styles";
import {FieldDataType} from "../../../../../generated/api/base";
import {extractEmailFromRenderedUser} from "../../../../../data/models/field/datatypes/PeopleSingle";
import type {User} from "../../../../../data/models/User";
import {PeopleMultipleChoiceInputV5} from "../PeopleMultipleChoiceInputV5";
import {Functions} from "../../../../../utils/function/Functions";
import {Spinner, SpinnerStyled} from "../../../loader/Spinner";
import {PeopleSingleChoiceInputV5} from "../PeopleSingleChoiceInputV5";
import {MassUpdatePopupV5} from "./MassUpdatePopupV5";

interface IMassFieldV5Props {
	readonly field: IFieldAdapter;
	readonly items: IModel[];
	readonly disabled: boolean;
	readonly icon: FieldIconName;
}

// TODO: spinner
// TODO: render the massField correctly

export const MassFieldV5 = (props: IMassFieldV5Props) => {
	const {field, items, disabled, icon} = props;
	const appState = useAppStore((store) => store.appState);

	const [isOpen, setIsOpen] = useState<boolean>(false);

	const onFieldClick = () => {
		if (!isAnyBeingUpdated) {
			setIsOpen(!isOpen);
		}
	};

	const isAnyBeingUpdated = appState.actions.isAnyItemFieldBeingUpdated(items, field);

	const renderContent = (): ReactNode => {
		const {allValuesMatch, firstValue} = getMassUpdateOptions(items, field, appState);

		let innerContentMaybe: ReactNode = <></>;

		if (allValuesMatch) {
			if (field.dataType === FieldDataType.PeopleMultipleChoiceList) {
				const usersData = firstValue.split(", ");
				const users: User[] = [];
				for (const data of usersData) {
					const userEmail = extractEmailFromRenderedUser(data);
					if (userEmail) {
						const userMaybe = appState.actions.getUserByEmail(userEmail);
						if (userMaybe) {
							users.push(userMaybe);
						}
					}
				}

				if (users.length > 0) {
					return (
						<PeopleMultipleChoiceInputV5
							value={users}
							onClick={onFieldClick}
							disabled={true}
							runOnClickEvenWhenDisabled={!isAnyBeingUpdated}
							onChange={Functions.emptyFunction} // can/should be edited from the popout
						/>
					);
				}
			} else {
				if (field.dataType === FieldDataType.PeopleSingleChoiceList) {
					const userEmail = extractEmailFromRenderedUser(firstValue);
					if (userEmail) {
						const userMaybe = appState.actions.getUserByEmail(userEmail);

						return (
							<PeopleSingleChoiceInputV5
								value={userMaybe}
								onClick={onFieldClick}
								disabled={true}
								runOnClickEvenWhenDisabled={!isAnyBeingUpdated}
								onChange={Functions.emptyFunction} // can/should be edited from the popout
							/>
						);
					}
				}
				innerContentMaybe = firstValue;
			}
		} else {
			innerContentMaybe = <i>Multiple values</i>;
		}

		return (
			<MassUpdateFieldValueStyled
				className={ReactUtils.cls("unfocused", {disabled})}
				onClick={onFieldClick}
			>
				{innerContentMaybe}
			</MassUpdateFieldValueStyled>
		);
	};

	return (
		<>
			<FieldV5
				key={field.refId}
				label={field.name}
				disabled={disabled || isAnyBeingUpdated}
				noWrap={true}
				icons={{preLabelIcon: getIconComponentFromFieldIconName(icon)}}
				tooltips={{
					labelTooltip: field.helperText,
					preLabelIconTooltip: getTooltipByIcon(icon),
				}}
			>
				<FieldContentStyled>
					{renderContent()}
					<Spinner isVisible={isAnyBeingUpdated} />
				</FieldContentStyled>
			</FieldV5>
			{isOpen && (
				<MassUpdatePopupV5
					fieldRefId={field.refId}
					items={items}
					onClose={onFieldClick}
				/>
			)}
		</>
	);
};

const FieldContentStyled = styled.div`
	display: contents;
	${SpinnerStyled} {
		position: absolute;
		top: 8px;
		right: 4px;
	}
`;

const MassUpdateFieldValueStyled = styled.div`
	min-height: 32px;
	width: 100%;
	border: 1px solid ${colorPalette.gray.c300};
	border-radius: ${radius.sm};
	${FlexCenterStyle}
	padding-left: ${baseDistance.sm};
	cursor: pointer;

	&:hover {
		background-color: ${colorPalette.gray.c200Light};
	}
`;
